import React from 'react';
import { Box, Typography } from '@mui/material';
import { dsVar, ink } from '../../../theme/tokens';

/** Shared styling for the change-set screens, in the Ontology pages' palette. */

export const PRIMARY_BUTTON_SX = {
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: dsVar('accent'),
    color: dsVar('on-accent'),
    '&:hover': { backgroundColor: '#F5D149' },
    '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') },
} as const;

export const SECONDARY_BUTTON_SX = { color: dsVar('fg-2'), textTransform: 'none' } as const;

export const LABEL_SX = {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: dsVar('fg-muted'),
    mb: 0.5,
} as const;

export const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: dsVar('page'), color: dsVar('fg-body'), fontSize: '0.875rem',
        '& fieldset': { borderColor: dsVar('line') },
        '&:hover fieldset': { borderColor: dsVar('line') },
        '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
    },
    '& .MuiInputLabel-root': { color: dsVar('fg-muted'), fontSize: '0.875rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: dsVar('accent-fg') },
    '& .MuiFormHelperText-root': { color: dsVar('fg-muted'), fontSize: '0.75rem' },
} as const;

export const DIALOG_PAPER_SX = {
    backgroundColor: dsVar('surface'), color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}`, backgroundImage: 'none',
} as const;

export const MONO_SX = { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem' } as const;

/** A small outlined label in one colour. */
export const Pill: React.FC<{ color: string; children: React.ReactNode; title?: string }> = ({ color, children, title }) => (
    <Box
        component="span"
        title={title}
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: 0.875, py: 0.125, borderRadius: '999px',
            border: `1px solid ${color}55`, backgroundColor: `${color}14`, color: ink(color),
            fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.6, whiteSpace: 'nowrap',
        }}
    >
        {children}
    </Box>
);

/** A titled block inside the review drawer. */
export const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
    <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
            <Typography sx={{ ...LABEL_SX, mb: 0 }}>{title}</Typography>
            {action}
        </Box>
        {children}
    </Box>
);

/** A tinted notice: info, warning or error. */
export const Notice: React.FC<{ tone?: 'info' | 'warning' | 'error' | 'success'; children: React.ReactNode }> = ({ tone = 'info', children }) => {
    const color = { info: '#60A5FA', warning: '#FBBF24', error: '#F87171', success: '#4ADE80' }[tone];
    return (
        <Box sx={{
            p: 1.25, borderRadius: '6px', border: `1px solid ${color}59`, backgroundColor: `${color}14`,
            fontSize: '0.8125rem', color: dsVar('fg-body'), lineHeight: 1.6,
        }}
        >
            {children}
        </Box>
    );
};
